import { HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ApiGateway } from "api/api-gateway";
import { AuthService } from "app/services/auth.service";
import { PageOptions } from "../interface/pageOptions";

@Injectable()
export class ScheduleService {
    constructor(private apiGateway: ApiGateway, private authService: AuthService) {}

    getUnityList(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.apiGateway
                .get("company/unityCompany")
                .subscribe((res: HttpResponse<any>) => {
                    if (res.body) {
                        resolve(res.body);
                    } else {
                        resolve(res);
                    }
                }, reject);
        });
    }

    getInsuranceList(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.apiGateway
                .get("insurance/app/list")
                .subscribe((res: HttpResponse<any>) => {
                    if (res.body) {
                        resolve(res.body);
                    } else {
                        resolve(res);
                    }
                }, reject);
        });
    }

    getInsurancePageSimpleList(pageOptions: PageOptions): Promise<any> {
        let urlPath = "insurance/search/portal";

        return new Promise((resolve, reject) => {
            this.apiGateway.post(urlPath, pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => reject(err),
            });
        });
    }

    getInsuranceById(id: number): Promise<any> {
        let urlPath = "insurance/app/" + id;
        return new Promise((resolve, reject) => {
            this.apiGateway.get(urlPath).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => reject(err),
            });
        });
    }

    getSchedulePageSimpleList(
        pageOptions: PageOptions,
        performerId?: number,
        companyId?: number,
        specialityId?: number,
        insuranceId?: number
    ): Promise<any> {
        const params: any = {};

        if (performerId != null) params.performerId = performerId;
        if (companyId != null) params.companyId = companyId;
        if (specialityId != null) params.specialityId = specialityId;
        if (insuranceId != null) params.insuranceId = insuranceId;

        const queryString = new URLSearchParams(params).toString();
        const urlPath = `scheduling/app/search${
            queryString ? "?" + queryString : ""
        }`;

        return this.apiGateway.post(urlPath, pageOptions).toPromise();
    }

    getSpecialistyPageSimpleList(
        pageOptions: PageOptions,
        companyId?: number
    ): Promise<any> {
        let urlPath = "speciality/search/portal";
        if (companyId != null) {
            urlPath += `?companyId=${companyId}`;
        }

        return new Promise((resolve, reject) => {
            this.apiGateway.post(urlPath, pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => reject(err),
            });
        });
    }

    getProcedurePageSimpleList(
        pageOptions: PageOptions,
        insuranceId: any,
        professionalId: any,
        companyId: any
    ): Promise<any> {
        const urlPath = "attendance/portal/procedure/searchByFilter";

        const body = {
            insuranceId: insuranceId,
            performerId: professionalId || "",
            companyId: companyId,
            pageSearchTO: pageOptions,
        };

        return new Promise((resolve, reject) => {
            this.apiGateway.post(urlPath, body).subscribe({
                next: (res: HttpResponse<any>) => resolve(res),
                error: (err) => reject(err),
            });
        });
    }

    getProfessionalPageSimpleList(
        pageOptions: PageOptions,
        specialityId: any,
        insuranceId: any
    ): Promise<any> {
        let urlPath = "professional/search/portal/" + specialityId + "/" + insuranceId;

        return new Promise((resolve, reject) => {
            this.apiGateway.post(urlPath, pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => reject(err),
            });
        });
    }

    getPerformerList(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.apiGateway
                .get(`professional/app/list`)
                .subscribe((res: HttpResponse<any>) => {
                    if (res.body) {
                        resolve(res.body);
                    } else {
                        resolve(res);
                    }
                }, reject);
        });
    }

    getProcedureList(insuranceId, professionalId?): Promise<any> {
        const qryProfessional = professionalId ? `?p=${professionalId}` : "";
        return new Promise((resolve, reject) => {
            this.apiGateway
                .get("accreditation/app/" + insuranceId + qryProfessional)
                .subscribe((res: HttpResponse<any>) => {
                    if (res.body) {
                        resolve(res.body);
                    } else {
                        resolve(res);
                    }
                }, reject);
        });
    }

    getSpecialityList(filter?: string): Promise<any> {
        filter = filter ? filter : "";
        return new Promise((resolve, reject) => {
            this.apiGateway.get(`speciality/app/list`).subscribe({
                next: (res: HttpResponse<any[]>) => {
                    res.body ? resolve(res.body) : resolve(res);
                },
                error(error) {
                    reject(error);
                },
            });
        });
    }

    getScheduleList(patientId, type: string, pageOptions): Promise<any> {
        let urlPath = `scheduling/app/${patientId}/${type}`;

        return new Promise((resolve, reject) => {
            this.apiGateway.post(urlPath, pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    getSpecialityInternal(pageOptions): Promise<any> {
        let urlPath = "speciality/search/internal";

        return new Promise((resolve, reject) => {
            this.apiGateway.post(urlPath, pageOptions).subscribe({
                next: (res: HttpResponse<any>) => {
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    checkScheduleLimit(idPaciente: any): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.apiGateway
                .get(`scheduling/app/limit/${idPaciente}`)
                .subscribe({
                    next: (res: HttpResponse<any[]>) => {
                        res.body ? resolve(res.body) : resolve(res);
                    },
                    error(error) {
                        reject(error);
                    },
                });
        });
    }

    findScheduleDates(performerId: number, companyId: number): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.apiGateway
                .get(`scheduling/app/search/date/${performerId}/${companyId}`)
                .subscribe({
                    next: (res: HttpResponse<any[]>) => {
                        res.body ? resolve(res.body) : resolve(res);
                    },
                    error(error) {
                        reject(error);
                    },
                });
        });
    }

    findScheduleHours(
        performerId: number,
        companyId: number,
        date: any
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.apiGateway
                .get(
                    `scheduling/app/search/hour/${performerId}/${companyId}/${date}`
                )
                .subscribe({
                    next: (res: HttpResponse<any[]>) => {
                        res.body ? resolve(res.body) : resolve(res);
                    },
                    error(error) {
                        reject(error);
                    },
                });
        });
    }

    getPerformerAllPageNoEntity(
        pageOptions: PageOptions,
        specialityId: number,
        idUnidade: number,
        insuranceId: number
    ): Promise<any> {
        let path =
            "professional/performer/search/app/" +
            specialityId +
            "/" +
            idUnidade +
            "/" +
            insuranceId;
        return new Promise((resolve, reject) => {
            this.apiGateway
                .post(path, pageOptions)
                .subscribe((res: HttpResponse<any>) => {
                    resolve(res);
                }, reject);
        });
    }

    getProcedureByInsuranceAndPerformer(
        insuranceId: number,
        performerId: any,
        companyId: any,
        pageOptions: PageOptions
    ): Promise<any> {
        const body = {
            insuranceId: insuranceId,
            performerId: performerId || "",
            companyId: companyId || "",
            pageSearchTO: pageOptions,
        };
        return new Promise((resolve, reject) => {
            this.apiGateway
                .post(`attendance/portal/procedure/searchByFilter`, body)
                .subscribe({
                    next: (res: HttpResponse<any>) => {
                        resolve(res);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    consultaAgendamentoPorUnidadeEspecialidade(idUnidade, idEspecialidade) {
        return new Promise<any>((resolve, reject) => {
            this.apiGateway
                .get(
                    `datasnap/rest/TCalendarController/consultaAgendamentoUnidadeEspecialidade/${idUnidade}/${idEspecialidade}`
                )
                .subscribe((res: HttpResponse<any>) => {
                    if (res.body) {
                        resolve(res.body);
                    } else {
                        resolve(res);
                    }
                }, reject);
        });
    }

    saveSchedule(waitingListRegistration): Promise<any> {
        return this.authService.runWithAdminToken(
            () =>
                new Promise<any>((resolve, reject) => {
                    this.apiGateway
                        .post("scheduling/save/app", waitingListRegistration)
                        .subscribe({
                            next: (res: HttpResponse<any[]>) => {
                                res.body ? resolve(res.body) : resolve(res);
                            },
                            error: (err) => {
                                reject(err);
                            },
                        });
                })
        );
    }

    saveScheduleUnregistered(schedule): Promise<any> {
        return this.authService.runWithAdminToken(
            () =>
                new Promise<any>((resolve, reject) => {
                    this.apiGateway
                        .post("scheduling/save/app/unregistered", schedule)
                        .subscribe({
                            next: (res: HttpResponse<any[]>) => {
                                res.body ? resolve(res.body) : resolve(res);
                            },
                            error: (err) => {
                                reject(err);
                            },
                        });
                })
        );
    }

    saveSchedulsse(schedule) {
        return new Promise((resolve, reject) => {
            this.apiGateway
                .post(`scheduling/save/app/`, schedule)
                .subscribe((res: HttpResponse<any>) => {
                    if (res.body) {
                        resolve(res.body);
                    } else {
                        resolve(res);
                    }
                }, reject);
        });
    }

    buscarAgendaporId(idAgenda: number) {
        return new Promise((resolve, reject) => {
            this.apiGateway
                .get(`datasnap/rest/TCalendarController/agenda/${idAgenda}`)
                .subscribe({
                    next: (res) => {
                        if (res.body) {
                            resolve(res.body);
                        } else {
                            resolve(res);
                        }
                    },
                    error(err) {
                        reject(err);
                    },
                });
        });
    }

    registerScheduleUsage(idAgenda: number) {
        return this.authService.runWithAdminToken(
            () =>
                new Promise((resolve, reject) => {
                    this.apiGateway
                        .post(`scheduling/registerScheduleUsage/${idAgenda}`)
                        .subscribe({
                            next: (res) => {
                                if (res.body) {
                                    resolve(res.body);
                                } else {
                                    resolve(res);
                                }
                            },
                            error(err) {
                                reject(err);
                            },
                        });
                })
        );
    }

    unregisterScheduleUsage(idAgenda: number) {
        return this.authService.runWithAdminToken(
            () =>
                new Promise((resolve, reject) => {
                    this.apiGateway
                        .post(`scheduling/unregisterScheduleUsage/${idAgenda}`)
                        .subscribe({
                            next: (res) => {
                                if (res.body) {
                                    resolve(res.body);
                                } else {
                                    resolve(res);
                                }
                            },
                            error(err) {
                                reject(err);
                            },
                        });
                })
        );
    }
}
